import { useEffect, useId, useRef, useState } from 'react'
import { Link, NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { Workspace, WorkspacesResponse } from '../api/types'
import { planDenialMessage, useWorkspaceCreateGate } from '../entitlements'
import { CreditsBanner } from '../entitlements/CreditsBanner'
import { PlanNote } from '../entitlements/PlanNote'
import { Modal } from './Modal'
import { useSession } from '../context/SessionContext'
import { can } from '../rbac'
import { Icon, SymoneMark } from './Icon'
import { navGroups, type NavItem } from './nav'
import { RequireDeveloperMode } from './DeveloperRoute'
import { ModeProvider, useMode } from '../context/ModeContext'
import { InviteBanner } from './InviteBanner'
import { GettingStartedView } from '../views/GettingStartedView'
import { ApiKeysView } from '../views/ApiKeysView'
import { ToolkitsView } from '../views/ToolkitsView'
import { TriggersView } from '../views/TriggersView'
import { LogsView } from '../views/LogsView'
import { PlaygroundView } from '../views/PlaygroundView'
import { DirectoryView } from '../views/DirectoryView'
import { SessionsView } from '../views/SessionsView'
import { SettingsView } from '../views/SettingsView'
import { PermissionsView } from '../views/PermissionsView'
// rbac-restore R2 (developer mode)
import { WebhooksView } from '../views/WebhooksView'
// rbac-restore R3 (developer mode)
import { SkillsView } from '../views/SkillsView'
import { EndUsersView } from '../views/EndUsersView'
import { AuthConfigsView } from '../views/AuthConfigsView'
import { ComingSoonView } from '../views/ComingSoonView'
import { COMING_SOON } from '../views/comingSoonContent'
import { AppsView } from '../views/AppsView'
import { ConnectClaudeView } from '../views/ConnectClaudeView'
import { AccountView } from '../views/AccountView'
import { TeamView } from '../views/TeamView'
import { WorkspaceView } from '../views/WorkspaceView'
import { PlansView } from '../views/PlansView'
import './Shell.css'

const DOCS_URL: string | undefined = import.meta.env.VITE_DOCS_URL
const SUPPORT_URL: string | undefined = import.meta.env.VITE_SUPPORT_URL

const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' }

// Workspace switcher (simple-mode S3 3.3, D33): each workspace with the caller's role,
// "Create workspace…" and a "Workspace settings" entry.
function WorkspaceSwitcher() {
  const { workspaceId, switchWorkspace } = useSession()
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const { data } = useQuery({
    queryKey: qk.workspaces,
    queryFn: () => apiFetch<WorkspacesResponse>('/workspaces'),
  })
  const workspaces = data?.workspaces ?? []
  const id = useId()

  return (
    <div className="ws-switcher">
      <label htmlFor={id} className="sr-only">Workspace</label>
      <select
        id={id}
        className="ws-select"
        value={workspaceId ?? ''}
        onChange={(e) => {
          setError(null)
          switchWorkspace(e.target.value).catch(() => setError('Couldn’t switch workspace.'))
        }}
      >
        {workspaces.length === 0 && <option value={workspaceId ?? ''}>Current workspace</option>}
        {workspaces.map((w) => (
          <option key={w.id} value={w.id}>
            {w.role ? `${w.name} · ${ROLE_LABEL[w.role] ?? w.role}` : w.name}
          </option>
        ))}
      </select>
      <Icon name="chevronDown" size={14} className="ws-select-chevron" />
      <div className="ws-actions">
        <Link to="/workspace" className="link-btn">Workspace settings</Link>
        <button type="button" className="link-btn" onClick={() => setCreating(true)}>
          Create workspace…
        </button>
      </div>
      {error && <p className="ws-error" role="alert">{error}</p>}
      {creating && <CreateWorkspaceDialog onClose={() => setCreating(false)} />}
    </div>
  )
}

// POST /workspaces {name}, then switch the session to it (POST /auth/active-workspace).
// The plan limit `workspaces.max` is checked by the gateway against the caller's OWNED
// workspaces and the best of their plans (enforcement/limits.py check_workspace_limit).
// GET /entitlements.account carries those same inputs (S4), so Create is pre-disabled
// with the server's remedy when the caller is at the limit (and while the plan is
// loading or unreadable: fail closed). A 403 `X-Entitlement-Denied: workspaces.max`
// still shows the server's remedy and nothing switches.
function CreateWorkspaceDialog({ onClose }: { onClose: () => void }) {
  const { switchWorkspace } = useSession()
  const gate = useWorkspaceCreateGate()
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: async (wsName: string) => {
      const ws = await apiFetch<Workspace>('/workspaces', { method: 'POST', body: JSON.stringify({ name: wsName }) })
      if (!ws?.id) throw new Error('The workspace was created but its id was not returned. Reload to see it.')
      return ws
    },
    onSuccess: async (ws) => {
      await switchWorkspace(ws.id)
    },
  })
  const trimmed = name.trim()
  return (
    <Modal title="Create workspace" onClose={onClose}>
      <form
        aria-label="Create workspace form"
        onSubmit={(e) => {
          e.preventDefault()
          if (!trimmed || create.isPending || !gate.allowed) return
          create.mutate(trimmed)
        }}
      >
        <div className="field">
          <label htmlFor="ws-create-name">Workspace name</label>
          <input id="ws-create-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>
        <PlanNote gate={gate} id="ws-create-limit" />
        {create.error ? (
          <p className="form-error" role="alert">{planDenialMessage(create.error) ?? errorMessage(create.error)}</p>
        ) : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={!trimmed || create.isPending || !gate.allowed}
            aria-busy={create.isPending}
            aria-describedby={!gate.allowed && !gate.loading ? 'ws-create-limit' : undefined}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function NavSearch({ items }: { items: NavItem[] }) {
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  const matches = q.trim()
    ? items.filter((n) => n.label.toLowerCase().includes(q.trim().toLowerCase()))
    : []

  return (
    <div className="nav-search">
      <Icon name="search" size={14} className="nav-search-icon" />
      <input
        type="search"
        className="nav-search-input"
        placeholder="Search"
        aria-label="Search pages"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && matches[0]) {
            navigate(matches[0].path)
            setQ('')
          }
          if (e.key === 'Escape') setQ('')
        }}
      />
      {matches.length > 0 && (
        <ul className="nav-search-results" role="listbox" aria-label="Matching pages">
          {matches.map((m) => (
            <li key={m.path} role="option" aria-selected={false}>
              <button type="button" onClick={() => { navigate(m.path); setQ('') }}>
                <Icon name={m.icon} size={14} /> {m.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function AccountMenu() {
  const { session, logout } = useSession()
  const mode = useMode()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { data } = useQuery({
    queryKey: qk.workspaces,
    queryFn: () => apiFetch<WorkspacesResponse>('/workspaces'),
  })
  const ws = data?.workspaces?.find((w) => w.id === session?.workspace.id)
  const user = session?.user
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Account'

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="account" ref={ref}>
      <button
        type="button"
        className="account-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="account-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
        <span className="account-text">
          <span className="account-name">{name}</span>
          <span className="account-ws">{ws?.name ?? session?.workspace.role ?? ''}</span>
        </span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="account-menu" role="menu">
          <div className="account-menu-meta">
            <span>{user?.email}</span>
            <span className="muted">{session?.workspace.role} · {session?.workspace.plan} plan</span>
          </div>
          <button type="button" role="menuitem" className="account-menu-item" onClick={() => { setOpen(false); navigate('/account') }}>
            <Icon name="settings" size={14} /> Account
          </button>
          {mode.entitled && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={mode.preference}
              className="account-menu-item"
              disabled={mode.saving}
              onClick={() => void mode.setDeveloperMode(!mode.preference).catch(() => undefined)}
            >
              <Icon name={mode.preference ? 'check' : 'box'} size={14} /> Developer mode
            </button>
          )}
          <button type="button" role="menuitem" className="account-menu-item" onClick={() => void logout()}>
            <Icon name="logout" size={14} /> Logout
          </button>
        </div>
      )}
    </div>
  )
}

function FooterLink({ href, icon, label }: { href?: string; icon: 'help' | 'book'; label: string }) {
  if (!href) {
    return (
      <span className="shell-nav-link shell-nav-link--muted" title={`${label} link not configured`}>
        <Icon name={icon} /> {label}
      </span>
    )
  }
  return (
    <a className="shell-nav-link" href={href} target="_blank" rel="noreferrer">
      <Icon name={icon} /> {label}
      <Icon name="external" size={12} className="shell-nav-ext" />
    </a>
  )
}

export function Shell() {
  return (
    <ModeProvider>
      <ShellLayout />
    </ModeProvider>
  )
}

function ShellLayout() {
  const mode = useMode()
  const { session } = useSession()
  const groups = navGroups(mode.effective, mode.feature, (p) => can(session, p))
  return (
    <div className="shell">
      <nav className="shell-sidebar" aria-label="Main navigation">
        <div className="shell-brand">
          <SymoneMark />
          <span className="shell-brand-name">Symone</span>
          {mode.effective && <span className="mode-chip">Developer</span>}
        </div>

        <WorkspaceSwitcher />
        <NavSearch items={groups.flat()} />

        {groups.map((group, i) => (
          <div key={i} className="shell-nav-group">
            {i > 0 && <hr className="shell-divider" />}
            {group.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => 'shell-nav-link' + (isActive ? ' active' : '')}
              >
                <Icon name={item.icon} />
                <span>{item.label}</span>
                {item.soon && <span className="soon-dot" title="Coming soon" aria-label="coming soon" />}
              </NavLink>
            ))}
          </div>
        ))}

        <div className="shell-spacer" />

        <div className="shell-footer">
          <FooterLink href={SUPPORT_URL} icon="help" label="Support" />
          <FooterLink href={DOCS_URL} icon="book" label="Documentation" />
          <AccountMenu />
        </div>
      </nav>

      <main className="shell-main" id="main-content">
        <InviteBanner />
        <CreditsBanner />
        <Routes>
          <Route index element={<Navigate to="/apps" replace />} />
          {/* Simple mode (D31): every user. */}
          <Route path="/apps" element={<AppsView />} />
          <Route path="/connect-claude" element={<ConnectClaudeView />} />
          <Route path="/account" element={<AccountView />} />
          <Route path="/team" element={<TeamView />} />
          <Route path="/workspace" element={<WorkspaceView />} />
          {/* Where every upsell link points (S4; Owner ruling on the CTA pending). */}
          <Route path="/plans" element={<PlansView />} />
          {/* Role × permission matrix (rbac-restore R1), reached from Team in either mode. */}
          <Route path="/permissions" element={<PermissionsView />} />
          {/* Developer mode (D30): every developer page goes INSIDE this guard. When
              Developer mode is not effective it redirects to /apps with a notice. */}
          <Route element={<RequireDeveloperMode />}>
            <Route path="/getting-started" element={<GettingStartedView />} />
            <Route path="/api-keys" element={<ApiKeysView />} />
            <Route path="/toolkits" element={<ToolkitsView />} />
            <Route path="/triggers" element={<TriggersView />} />
            <Route path="/logs" element={<LogsView />} />
            <Route path="/playground" element={<PlaygroundView />} />
            <Route path="/directory" element={<DirectoryView />} />
            <Route path="/sessions" element={<SessionsView />} />
            <Route path="/settings" element={<SettingsView />} />
            {/* rbac-restore R2 (developer mode) */}
            <Route path="/webhooks" element={<WebhooksView />} />
            {/* rbac-restore R3 (developer mode) */}
            <Route path="/skills" element={<SkillsView />} />
            <Route path="/users" element={<EndUsersView />} />
            <Route path="/auth-configs" element={<AuthConfigsView />} />
            {Object.entries(COMING_SOON).map(([path, content]) => (
              <Route key={path} path={path} element={<ComingSoonView {...content} />} />
            ))}
          </Route>
          <Route path="*" element={<Navigate to="/apps" replace />} />
        </Routes>
      </main>
    </div>
  )
}
